import _ from "lodash";

import type { FloorSpaceCatalog } from "@/models/floor-space-catalog";
import type Light from "@/models/light";
import colors from "@/styles/colors";

import { getFirmwareColor } from "./firmware-colors";

/** `filtered` is set externally by the filter/search flows, not by the Light model. */
type FilterableLight = Light & { filtered?: boolean };

export interface LightStyle {
    color: string;
    opacity: number;
    aperture: {
        color: string;
        opacity: number;
    };
}

export interface LightStyleContext {
    /**
     * Whether the filter panel is narrowing the visible lights.
     */
    filtering: boolean;
    /**
     * Whether a search is narrowing the visible lights.
     */
    searching: boolean;
    /**
     * The name of the active map mode.
     */
    modeName: string;
    /**
     * The configured positioning type, e.g. `VLC`, `BLE`, or `VLC + BLE`.
     */
    positioningType: string;
    /**
     * The FloorSpaceCatalog lookup map keyed by floorSpaceCatalogID.
     */
    floorSpaceCatalogMap: Record<number, FloorSpaceCatalog>;
}

const setLightOpacity = (
    light: FilterableLight,
    style: LightStyle,
    { filtering, searching }: LightStyleContext,
) => {
    style.opacity = (filtering || searching) && !light.filtered ? 0.1 : 0.25;
    style.aperture.opacity =
        (filtering || searching) && !light.filtered ? 0.1 : 1;
};

const setLightColor = (
    light: Light,
    defaultColor: string,
    style: LightStyle,
    { modeName, positioningType, floorSpaceCatalogMap }: LightStyleContext,
) => {
    if (modeName === "firmware") {
        if (light.selected) {
            style.color = colors.skyHover;
            return;
        }

        const catalogId = light.options?.floorSpaceCatalog_ID;
        const catalog = catalogId ? floorSpaceCatalogMap[catalogId] : null;

        style.color = getFirmwareColor(catalog?.firmwareVersion);

        return;
    }

    const hasFlag = (label: string) =>
        _.find(light.flags, (flag) => flag.label === label);

    style.color = defaultColor;

    if (light.selected) {
        style.color = colors.skyHover;
        return;
    }

    let color = "";

    if (light.flags.length > 0) {
        const bleOnlyFlags = _.find(
            light.flags,
            (flag) => flag.value === 128 || flag.value === 32,
        );

        const vlcOnlyFlags = _.find(light.flags, (flag) => flag.value === 16);

        if (hasFlag("Out of Scope") || hasFlag("Not LED")) {
            color = colors.cautionRed;
        } else if (
            hasFlag("Emergency Light") &&
            !hasFlag("Missing Location Description") &&
            !hasFlag("No VLC Signal") &&
            !hasFlag("Full Outage") &&
            !hasFlag("Damaged") &&
            !hasFlag("Partial Outage") &&
            !hasFlag("Weak Antenna")
        ) {
            color = defaultColor;
        } else if (bleOnlyFlags && positioningType === "VLC") {
            color = defaultColor;
        } else if (vlcOnlyFlags && positioningType === "BLE") {
            color = defaultColor;
        } else {
            color = colors.cautionRed;
        }
    }

    if (color !== colors.cautionRed) {
        _.each(positioningType.split(" + "), (type) => {
            const prop = type === "VLC" ? "id" : "bleId";

            if (!_.toInteger(light[prop])) {
                color = colors.attentionYellow;
            }
        });
    }

    const highlighted = _.find(light.flags, (flag) => flag.value === 256);

    if (highlighted) {
        color = colors.marketingPurple;
    }

    if (color) {
        style.color = color;
    }
};

/**
 * Derives the fill and signal aperture styling for a light from the current map context.
 *
 * @param light - The light to style.
 * @param defaultColor - The fallback color when no flag or mode overrides it.
 * @param context - The map state that influences styling.
 * @returns The resolved style for the light.
 */
export const getLightStyle = (
    light: Light,
    defaultColor: string,
    context: LightStyleContext,
): LightStyle => {
    const style: LightStyle = {
        color: defaultColor,
        opacity: 0.25,
        aperture: {
            color: defaultColor,
            opacity: 1,
        },
    };

    setLightOpacity(light, style, context);
    setLightColor(light, defaultColor, style, context);
    style.aperture.color = style.color;

    return style;
};
